import type { MaybeRefOrGetter } from 'vue';
import type { UserAvatarUser } from '#layers/access/types/user-avatar';
import type { Filter } from '#layers/data-model/public';
import { refDebounced } from '@vueuse/core';
import { computed, onMounted, readonly, ref, toRef, toValue, watch } from 'vue';
import { searchWorkspaceMembers } from '#layers/access/utils/api/workspace-members';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';

const MEMBER_SEARCH_DEBOUNCE_MS = 250;
const MEMBER_PAGE_SIZE = 10;

interface UseWorkspaceMemberSearchOptions {
	/** Reactive search text; debounced before hitting the server. */
	search: MaybeRefOrGetter<string>;
	/** Reactive filter AND-combined with the search (e.g. to omit already-connected members). */
	filter?: MaybeRefOrGetter<Filter | undefined>;
}

/**
 * Reactive, workspace-member-scoped user search backing the user picker. Fetches eagerly (so the
 * list is ready the first time the popover opens), debounces the search text, and ignores stale
 * in-flight responses so a slower earlier request can't clobber a fresher one. A failed request
 * surfaces through `error` instead of reading as an empty member list.
 *
 * Results are paged: `loadMore` appends the next page for infinite scroll, while a new search term
 * or filter restarts from the first page.
 */
export function useWorkspaceMemberSearch(options: UseWorkspaceMemberSearchOptions) {
	const search = toRef(options.search);
	const filter = toRef(options.filter);
	const debouncedSearch = refDebounced(search, MEMBER_SEARCH_DEBOUNCE_MS);
	const { fetch } = useWorkspaceRuntimeHandle();

	const members = ref<UserAvatarUser[]>([]);
	const totalCount = ref(0);
	const isLoading = ref(false);
	const isLoadingMore = ref(false);
	const error = ref<unknown>(null);
	let activeController: AbortController | undefined;

	const hasMore = computed(() => !error.value && members.value.length < totalCount.value);

	async function fetchPage(offset: number) {
		activeController?.abort();
		const controller = new AbortController();
		activeController = controller;
		const isFirstPage = offset === 0;
		isLoading.value = isFirstPage;
		isLoadingMore.value = !isFirstPage;
		error.value = null;

		try {
			const page = await searchWorkspaceMembers({
				fetch,
				search: debouncedSearch.value,
				filter: toValue(filter),
				limit: MEMBER_PAGE_SIZE,
				offset,
				signal: controller.signal,
			});

			if (activeController !== controller) return;
			members.value = isFirstPage ? page.members : [...members.value, ...page.members];
			totalCount.value = page.totalCount;
		}
		catch (requestError) {
			// A newer search superseded this one — which is also what aborted it, since `abort()` is only
			// ever called immediately before `activeController` is replaced. So getting past this guard
			// means a genuine failure on the newest request, not a cancellation.
			if (activeController !== controller) return;

			if (isFirstPage) {
				members.value = [];
				totalCount.value = 0;
			}

			error.value = requestError;
		}
		finally {
			if (activeController === controller) {
				isLoading.value = false;
				isLoadingMore.value = false;
				activeController = undefined;
			}
		}
	}

	function runSearch() {
		return fetchPage(0);
	}

	async function loadMore() {
		if (isLoading.value || isLoadingMore.value || !hasMore.value) return;
		await fetchPage(members.value.length);
	}

	// Refetch when the (debounced) search or filter changes. The initial load runs in onMounted rather
	// than during setup so the command palette picks up members that arrive after it has mounted.
	watch([debouncedSearch, filter], () => runSearch());
	onMounted(runSearch);

	return {
		members: readonly(members),
		isLoading: readonly(isLoading),
		error: readonly(error),
		hasMore,
		loadMore,
	};
}
